// Shared shell of the two Seller Sourcing History Swipers: header (icon, title, subtitle, View All ->),
// Swiper with discreet round Previous/Next buttons + pagination dots (both hidden automatically when
// everything fits: Swiper `watchOverflow`), keyboard + screen-reader labels (A11y module).
import React from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import useElakaySwiper from "../../slider/swiperSettings/useElakaySwiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import "./sellerHistory.css";

export const HistorySectionHeader = ({ icon: Icon, title, subtitle, viewAllTo, headingId, level = 2 }) => {
  const Heading = level === 1 ? "h1" : "h2";
  return (
  <header className="sph-head">
    <div className="sph-head__titles">
      {Icon && <span className="sph-head__icon" aria-hidden="true"><Icon /></span>}
      <div>
        <Heading id={headingId}>{title}</Heading>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
    {viewAllTo && <Link className="sph-head__all" to={viewAllTo}>{text("h_sph_view_all", "View All →")}</Link>}
  </header>
  );
};

// Each caller's slidesPerView/breakpoints are that instance's own layout (instance level);
// everything else comes from Animation Settings -> Swiper Settings ("Sourcing History Slider").
const historyLegacyTable = (slidesPerView, breakpoints) => {
  if (slidesPerView === undefined && !breakpoints) return null;
  const table = { 0: { slidesPerView: slidesPerView ?? 1, spaceBetween: 14 } };
  Object.entries(breakpoints || {}).forEach(([width, entry]) => {
    table[width] = { slidesPerView: entry?.slidesPerView ?? slidesPerView ?? 1, spaceBetween: entry?.spaceBetween ?? 14 };
  });
  return table;
};

export default function HistorySwiperSection({ id, icon, title, subtitle, viewAllTo, items, getKey, renderItem, breakpoints, slidesPerView, swiperOptions }) {
  const headingId = `${id}-title`;
  const legacyTable = historyLegacyTable(slidesPerView, breakpoints);
  const swp = useElakaySwiper("providerHistory", {
    swiperOptions,
    legacyTable,
    legacyExplicit: Boolean(legacyTable),
    slideCount: items?.length || 0,
    originPatch: { pagination: { dynamicBullets: (items?.length || 0) > 8 } },
  });
  return (
    <section className={`sph-block sph-block--${id}`} aria-labelledby={headingId}>
      <HistorySectionHeader icon={icon} title={title} subtitle={subtitle} viewAllTo={viewAllTo} headingId={headingId} />
      <Swiper
        key={swp.key}
        {...swp.swiperProps}
        className={`sph-swiper ${swp.className}`}
        style={swp.style}
        a11y={{
          prevSlideMessage: text("h_sph_prev", "Previous"),
          nextSlideMessage: text("h_sph_next", "Next"),
          containerMessage: title,
          paginationBulletMessage: text("h_sph_go_to", "Go to slide {{index}}"),
        }}
      >
        {items.map((item, index) => <SwiperSlide key={getKey(item, index)}>{renderItem(item)}</SwiperSlide>)}
      </Swiper>
    </section>
  );
}

// Loading: the section frame with card-shaped skeletons (same responsive column counts as the Swiper).
export const HistorySkeletonSection = ({ id, icon, title, subtitle, Skeleton, count }) => (
  <section className={`sph-block sph-block--${id}`} aria-busy="true" aria-labelledby={`${id}-title`}>
    <HistorySectionHeader icon={icon} title={title} subtitle={subtitle} headingId={`${id}-title`} />
    <div className={`sph-skeletons sph-skeletons--${id}`} role="status">
      <span className="spm-sr-only">{text("h_spm_loading", "Loading…")}</span>
      {Array.from({ length: count }, (_, i) => <Skeleton key={i} />)}
    </div>
  </section>
);

// A history failure never breaks the host page: a discreet line with Retry.
export const HistoryErrorSection = ({ id, icon, title, subtitle, onRetry }) => (
  <section className={`sph-block sph-block--${id}`} aria-labelledby={`${id}-title`}>
    <HistorySectionHeader icon={icon} title={title} subtitle={subtitle} headingId={`${id}-title`} />
    <div className="sph-error" role="alert">
      <span>{text("h_sph_error_short", "Unable to load recently viewed items.")}</span>
      <button type="button" onClick={onRetry}>{text("h_spm_retry", "Retry")}</button>
    </div>
  </section>
);
